import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { Box, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { NavLink } from 'react-router-dom'

import { BrandMark } from '@/components/common/BrandMark'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { hasPermission } from '@/features/auth/permissions'
import { color, motion as motionTokens } from '@/v2/design-system'
import { navGroupsV2 } from '@/v2/layouts/navigation'

export const SIDEBAR_V2_WIDTH = 224
export const SIDEBAR_V2_COLLAPSED_WIDTH = 72

interface SidebarV2Props {
  variant: 'permanent' | 'temporary'
  open: boolean
  onClose: () => void
  collapsed: boolean
  onToggleCollapsed: () => void
}

/**
 * Sidebar V2 — más angosto que el actual (232px), con modo compacto
 * (sección 5). REUTILIZA `useAuth()`/`hasPermission()` tal cual — cero
 * matriz de permisos nueva. La navegación viene de `navGroupsV2`
 * (v2/layouts/navigation.ts), que ya documenta qué rutas son nativas de V2
 * y cuáles apuntan a páginas comerciales existentes.
 */
export function SidebarV2({ variant, open, onClose, collapsed, onToggleCollapsed }: SidebarV2Props) {
  const { user } = useAuth()
  const width = collapsed ? SIDEBAR_V2_COLLAPSED_WIDTH : SIDEBAR_V2_WIDTH

  const visibleGroups = navGroupsV2
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.permission || hasPermission(user?.role, item.permission)),
    }))
    .filter((group) => group.items.length > 0)

  const content = (
    <Stack sx={{ height: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: collapsed ? 1.5 : 2.25, py: 2.25, justifyContent: collapsed ? 'center' : 'flex-start' }}
      >
        <BrandMark size={26} />
        {!collapsed && (
          // UX-9 Brand Phase B — identidad oficial reducida a
          // [ISOTIPO] + "GOVIA", sin subtítulo bajo el logo (regla de
          // marca del encargo: "Sistema de Inteligencia" retirado). El
          // wordmark sigue siendo <Typography> real (no una imagen
          // estática del logo horizontal) — accesible, seleccionable y
          // ya theming-consistente con el resto del shell; no es "otro
          // wordmark recreado a mano", es el mismo texto de marca que ya
          // existía aquí, solo sin la línea de debajo.
          <Typography variant="subtitle1" sx={{ lineHeight: 1.15, fontWeight: 700 }}>
            GOVIA
          </Typography>
        )}
      </Stack>

      <Stack
        component="nav"
        sx={{ px: collapsed ? 1 : 1.5, py: 1, flex: 1, overflowY: 'auto', gap: 1.5 }}
      >
        {visibleGroups.map((group) => (
          <Stack key={group.label} spacing={0.5}>
            {!collapsed && (
              <Typography
                variant="overline"
                color="text.disabled"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pb: 0.25,
                  fontSize: group.secondary ? '0.625rem' : undefined,
                  opacity: group.secondary ? 0.7 : 1,
                }}
              >
                {group.label}
              </Typography>
            )}
            {group.items.map((item) => {
              const Icon = item.icon

              const link = (
                <Box
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  end={item.path === '/v2'}
                  onClick={onClose}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    gap: 1.25,
                    px: collapsed ? 0 : 1.25,
                    py: 1,
                    borderRadius: 2,
                    color: 'text.secondary',
                    textDecoration: 'none',
                    transition: `background-color ${motionTokens.duration.base}ms ${motionTokens.easing.standard}`,
                    opacity: group.secondary ? 0.85 : 1,
                    '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.08)' },
                    '&.active': {
                      color: 'primary.light',
                      bgcolor: 'rgba(76, 111, 255, 0.14)',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${color.primary}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Icon fontSize="small" />
                  {!collapsed && (
                    <Typography variant="body2" sx={{ fontWeight: group.secondary ? 500 : 600 }} noWrap>
                      {item.label}
                    </Typography>
                  )}
                </Box>
              )
              return collapsed ? (
                <Tooltip key={item.path} title={item.label} placement="right">
                  {link}
                </Tooltip>
              ) : (
                link
              )
            })}
          </Stack>
        ))}
      </Stack>

      <Stack sx={{ px: 1, py: 1.5, alignItems: collapsed ? 'center' : 'flex-end' }}>
        <Tooltip title={collapsed ? 'Expandir' : 'Colapsar'} placement="right">
          <IconButton size="small" onClick={onToggleCollapsed} aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}>
            {collapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )

  if (variant === 'permanent') {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          transition: `width ${motionTokens.duration.base}ms ${motionTokens.easing.standard}`,
          [`& .MuiDrawer-paper`]: {
            width,
            boxSizing: 'border-box',
            transition: `width ${motionTokens.duration.base}ms ${motionTokens.easing.standard}`,
            overflowX: 'hidden',
          },
        }}
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ [`& .MuiDrawer-paper`]: { width: SIDEBAR_V2_WIDTH, boxSizing: 'border-box' } }}
    >
      {content}
    </Drawer>
  )
}
